<template>
  <div class="home">
    <div class="page-header">
      <h1>College Football Scores</h1>
    </div>

    <div v-if="isLoading" class="loading">
      <Loader class="animate-spin" :size="48" />
      <p>Loading scores...</p>
    </div>

    <div v-else-if="games.length === 0" class="no-games">
      <CalendarX :size="48" />
      <p>No games scheduled</p>
    </div>

    <div v-else>
      <div
        v-for="(dateGroup, date) in groupedGames"
        :key="date"
        class="date-section"
      >
        <div class="date-header">
          <h2>{{ formatDateHeader(date) }}</h2>
        </div>
        <div class="games-container">
          <div
            v-for="game in dateGroup"
            :key="game.id"
            class="game-card"
            :class="{ 'favorite-game': isFavoriteGame(game) }"
            :data-game-state="game.status"
            @click="goToGameDetail(game.id)"
          >
            <div class="game-header">
              <span class="game-time">{{
                formatGameTime(game.start_date)
              }}</span>
              <span class="game-status" :class="getStatusClass(game.status)">
                <Circle
                  v-if="game.status === 'in_progress'"
                  class="live-indicator"
                  :size="8"
                  fill="#ff6b6b"
                />
                {{ getStatusText(game) }}
              </span>
            </div>

            <div class="teams">
              <div
                v-for="(team, type) in {
                  home: game.home_team,
                  away: game.away_team,
                }"
                :key="team?.id || type"
                class="team"
                :class="`${type}-team`"
              >
                <div class="team-info">
                  <div class="team-logo">
                    <img
                      v-if="getTeamLogoUrl(team)"
                      :src="getTeamLogoUrl(team)"
                      :alt="team.school || team.name"
                      @error="handleImageError"
                    />
                    <div v-else class="logo-placeholder">
                      {{ getTeamInitial(team) }}
                    </div>
                  </div>
                  <div class="team-details">
                    <div class="team-name">
                      {{ team.school || team.name || "Unknown Team" }}
                      <span
                        v-if="team.rank && team.rank < 99"
                        class="team-rank"
                      >
                        #{{ team.rank }}
                      </span>
                    </div>
                    <div class="team-record">{{ team.conference || "" }}</div>
                  </div>
                  <div v-if="game.status !== 'scheduled'" class="team-score">
                    {{ team.points || 0 }}
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="
                game.venue ||
                (game.weather && game.weather.temperature) ||
                game.tv
              "
              class="game-footer"
            >
              <span v-if="game.venue" class="venue">
                {{ game.venue.name }}, {{ game.venue.city }}
              </span>
              <span
                v-if="game.weather && game.weather.temperature"
                class="weather"
              >
                <Thermometer :size="14" />
                {{ Math.round(game.weather.temperature) }}°F
                <span v-if="game.weather.description">{{
                  game.weather.description
                }}</span>
              </span>
              <span v-if="game.tv" class="tv-network">
                <Tv :size="14" />
                {{ game.tv }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadLocalGameData } from "../utils/localDataLoader";
import { cookieUtils } from "../utils/cookieUtils";
import { getTeamInitial, handleImageError, getTeamLogoUrl } from "../utils/teamUtils";
import { timezoneUtils } from "../utils/timezoneUtils";
import { Loader, CalendarX, Circle, Thermometer, Tv } from "lucide-vue-next";

export default {
  components: {
    Loader,
    CalendarX,
    Circle,
    Thermometer,
    Tv,
  },
  name: "HomeView",
  data() {
    return {
      games: [],
      isLoading: false,
      favoriteTeams: [],
    };
  },
  computed: {
    groupedGames() {
      const groups = {};
      this.games.forEach((game) => {
        // Convert UTC date to local date for proper grouping
        const gameDate = new Date(game.start_date);
        const localDate = gameDate.toLocaleDateString("en-CA"); // YYYY-MM-DD format
        if (!groups[localDate]) {
          groups[localDate] = [];
        }
        groups[localDate].push(game);
      });

      // Sort each group by time
      Object.keys(groups).forEach((date) => {
        groups[date].sort(
          (a, b) => new Date(a.start_date) - new Date(b.start_date)
        );
      });

      // Sort the dates chronologically
      const sortedGroups = {};
      Object.keys(groups)
        .sort()
        .forEach((date) => {
          sortedGroups[date] = groups[date];
        });

      return sortedGroups;
    },
    userTimezone() {
      return cookieUtils.getUserSelectedTimezone() || timezoneUtils.getUserTimezone();
    },
    use12HourFormat() {
      return cookieUtils.getUse12HourFormat();
    },
  },
  created() {
    this.fetchGames();
  },
  updated() {
    let firstInProgress = document.querySelector(
      '[data-game-state="in_progress"]'
    );
    if (firstInProgress) {
      firstInProgress.scrollIntoView();
    }
  },
  methods: {
    getTeamInitial,
    handleImageError,
    getTeamLogoUrl,
    async fetchGames() {
      this.isLoading = true;
      try {
        // Load all local game data
        this.games = await loadLocalGameData(); // No date parameter to get all games
        // Load favorite teams from cookie
        this.favoriteTeams = cookieUtils.getFavoriteTeams();
      } catch (error) {
        console.error("Error loading games:", error);
        this.games = [];
      } finally {
        this.isLoading = false;
      }
    },
    isFavoriteGame(game) {
      if (!game || !game.home_team || !game.away_team) return false;
      return [game.home_team, game.away_team].some(
        (team) =>
          team &&
          team.id &&
          this.favoriteTeams.some((fav) => fav.id === team.id)
      );
    },
    getStatusClass(status) {
      if (status === "scheduled") return "status-pre";
      if (status === "in_progress") return "status-in";
      if (status === "completed") return "status-post";
      return "";
    },
    getStatusText(game) {
      if (game.status === "scheduled") {
        return this.formatGameTime(game.start_date);
      } else if (game.status === "in_progress") {
        return game.clock ? `${game.period}Q - ${game.clock}` : "In Progress";
      } else if (game.status === "completed") {
        return "Final";
      }
      return game.status;
    },
    formatGameTime(dateString) {
      return timezoneUtils.formatTime(dateString, this.userTimezone, {
        hour12: this.use12HourFormat
      });
    },
    formatDateHeader(dateString) {
      return timezoneUtils.formatDateHeader(dateString + "T00:00:00", this.userTimezone);
    },
    goToGameDetail(gameId) {
      this.$router.push(`/game/${gameId}`);
    },
  },
};
</script>

<style scoped>
.home {
  width: 100%;
}

.date-section {
  margin-bottom: 3rem;
}

.date-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #30363d;
}

.date-header h2 {
  color: #c9d1d9;
  font-size: 1.5rem;
  margin: 0;
}

.page-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #30363d;
}

.page-header h1 {
  color: #c9d1d9;
  font-size: 2rem;
  margin: 0;
}

.loading,
.no-games {
  text-align: center;
  padding: 3rem;
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border: 1px solid #30363d;
}

.loading > *,
.no-games > * {
  color: #ff7b45;
  margin-bottom: 1rem;
}

.games-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.game-card {
  background-color: #161b22;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #30363d;
}

.game-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #0d1117;
  border-bottom: 1px solid #30363d;
}

.game-time {
  font-weight: 500;
  color: #8b949e;
}

.game-status {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-pre {
  background-color: #1e3a5f;
  color: #58a6ff;
}

.status-in {
  background-color: #4a3806;
  color: #ffd43b;
}

.status-post {
  background-color: #1e4620;
  color: #56d364;
}

.live-indicator {
  color: #ff6b6b;
  margin-right: 0.25rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.teams {
  padding: 1rem;
}

.team {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.team:last-child {
  margin-bottom: 0;
  padding-top: 0.75rem;
  border-top: 1px solid #30363d;
}

.team-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.team-logo {
  width: 40px;
  height: 40px;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-logo img {
  max-width: 100%;
  max-height: 100%;
}

.logo-placeholder {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #4a5568, #2d3748);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px;
  color: #e2e8f0;
  text-transform: uppercase;
}

.team-details {
  flex: 1;
}

.team-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: #e0e0e0;
}

.game-card.favorite-game {
  position: relative;
  border: 2px solid #ffea00;
  overflow: visible;
}

.game-card.favorite-game::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: linear-gradient(
    45deg,
    #ff7b00,
    #ff9500,
    #ffb700,
    #ffea00,
    #ff7b00,
    #ff9500,
    #ffea00
  );
  background-size: 400% 400%;
  animation: borderSweep 4s linear infinite;
  border-radius: 8px;
  z-index: -1;
}

.game-card.favorite-game::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 0px;
  right: 0px;
  bottom: 2px;
  background: #161b22;
  border-radius: 16px;
  z-index: 0;
}

.game-card.favorite-game > * {
  position: relative;
  z-index: 1;
}

.game-card.favorite-game:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(255, 212, 59, 0.4);
}

@keyframes borderSweep {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 400% 50%;
  }
}

.team-record {
  font-size: 0.875rem;
  color: #8b949e;
}

.team-rank {
  display: inline-block;
  background-color: transparent;
  color: #8b949e;
  font-size: 0.7rem;
  font-weight: 400;
  padding: 0;
  border-radius: 0;
  margin-left: 0.5rem;
  opacity: 0.8;
}

.team-score {
  font-size: 1.75rem;
  font-weight: 700;
  color: #e0e0e0;
  margin-left: 1rem;
  min-width: 3rem;
  text-align: center;
}

.game-card[data-game-state="in"] .team-score {
  color: #ffd43b;
  text-shadow: 0 0 8px rgba(255, 212, 59, 0.3);
}

.game-card[data-game-state="post"] .team-score {
  color: #56d364;
}

.game-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: #0d1117;
  border-top: 1px solid #30363d;
  font-size: 0.75rem;
  color: #8b949e;
  gap: 0.75rem;
}

.conference {
  font-weight: 500;
  color: #e0e0e0;
}

.weather,
.venue,
.tv-network {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #8b949e;
}

.venue {
  font-weight: 500;
  color: #e0e0e0;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.weather i,
.tv-network i {
  margin-right: 0.25rem;
}

@media (max-width: 768px) {
  .page-header {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }

  .page-header h1 {
    font-size: 1.5rem;
  }

  .date-header {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
  }

  .date-header h2 {
    font-size: 1.25rem;
  }

  .games-container {
    grid-template-columns: 1fr;
  }
}
</style>
